import * as Sentry from '@sentry/react';
import { Span } from '@sentry/react';
import { DashboardDetails, SentryIoTransactionDetails } from 'type-declarations';

/*-----react-portal-------*/
//Upgraded to Sentry's New Tracing API using their v7 -> v8 migration guide
//https://docs.sentry.io/platforms/javascript/migration/v7-to-v8/v8-new-performance-api/
export function startQuicksightSession(
	portalSessionId: string,
	pageSessionId: string,
	dashboard: string
): Span {
	let sentrySpan: Span | undefined = undefined;
	Sentry.withScope((scope) => {
		const page_session_start = new Date().toISOString();
		scope.setTag('portal_session_id', portalSessionId);
		scope.setTag('page_session_id', pageSessionId);
		scope.setTag('page_session_start', page_session_start);
		scope.setTag('ingest_version', 2.0);
		sentrySpan = Sentry.startInactiveSpan({
			name: `${dashboard} Session`,
			forceTransaction: true,
			attributes: { page_session_start: page_session_start },
		});
	});
	return sentrySpan!;
}

export function finishTransaction(sentrySpan: Span): void {
	sentrySpan.setAttribute('page_session_end', new Date().toISOString());
	sentrySpan.end();
}

export function startQuicksightDashboardSpan(stepSpan: Span, dashboard: DashboardDetails): Span {
	let dashboardSpan: Span | undefined = undefined;
	//We should be able to simplify this when we update to v8. The startInsactiveSpan method can take a stepSpan
	//as a paremeter in that version, so we don't need to rely on this callback.
	//https://docs.sentry.io/platforms/javascript/tracing/instrumentation/#starting-spans-as-children-of-a-specific-span
	Sentry.withActiveSpan(
		stepSpan,
		() =>
			(dashboardSpan = Sentry.startInactiveSpan({
				name: dashboard.tabText,
				attributes: { dashboard_id: dashboard.dashboardId, dashboard_type: dashboard.tabText },
			}))
	);
	return dashboardSpan!;
}

export function endSpan(span: Span): void {
	span.end();
}

/*-----react-tachyon-------*/
export function startInteractionTracing(transactionDetails: SentryIoTransactionDetails): Span {
	let sentrySpan: Span | undefined = undefined;
	Sentry.withScope((scope) => {
		scope.setTag('ingest_version', 2.0);
		scope.setTag(SentryTags.INTERACTIONID, transactionDetails.interactionId);
		scope.setTag(SentryTags.INTERACTIONTYPE, transactionDetails.interactionType);

		sentrySpan = Sentry.startInactiveSpan({
			name: transactionDetails.title,
			forceTransaction: true,
			attributes: {
				step_start: new Date().toISOString(),
				step_type: transactionDetails.stepType,
				user_abandoned: false,
				user_attempted: false,
				could_continue: false,
			},
		});
	});
	return sentrySpan!;
}

export function endStepSpan(stepSpan: Span, status: string): void {
	stepSpan.setAttribute(SentryTags.STEPCOMPLETE, new Date().toISOString());
	stepSpan.setAttribute(SentryTags.LEGACYSTATUS, status);
	stepSpan.end();
}

export enum SentryTags {
	INTERACTIONID = 'interaction_id',
	STEPTYPE = 'step_type',
	INTERACTIONTYPE = 'interaction_type',
	STEPSTART = 'step_start',
	USERATTEMPTED = 'user_attempted',
	STEPCOMPLETE = 'set_complete',
	COULDCONTINUE = 'could_continue',
	USERABANDONED = 'user_abandoned',
	LEGACYSTATUS = 'legacy_status',
}
